import * as vp from 'vopay';
import * as Yup from 'yup';
import moment from 'moment';
import { FunctionComponent, JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import { WithdrawAccountHandler, FormData } from './types';
import { buildWithdrawAccountRows } from './buildWidthdrawAccountRows.helper';
import { usePendingData } from '../../../providers/PendingData.provider';
import { useAccountInfo } from '../../../providers/AccountInfo.provider';
import styles from './WithdrawAccountForm.module.scss';
import { useAccountSummary } from '../../../providers/AccountSummary.provider';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import useFetchClientAccountBalance from '../../../hooks/useApi/useFetchClientAccountBalance';
import AccountCreationContext from '../AccountCreation/AccountCreationContext';
import { useLockedFeatureDialogue } from '../../../providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '../../../providers/LockedFeatures.provider';
import { useTranslation } from 'react-i18next';

const { Box, Table, TextField, Select, Toggle, Icon, Tooltip, DatepickerSimple } = vp;

type WithdrawAccountProps = {
  handlers: WithdrawAccountHandler;
  clientAccount?: vp.ClientAccount;
  defaultTouches: FormikTouched<FormData>;
  defaultValues: FormData;
  showSubmitButton: boolean;
  isUnderReview: boolean;
  setIsUnderReview: (e: boolean) => void;
  hasBankAccount: boolean;
};

type StartDateType = {
  parent: {
    StartDate: string;
  };
};
const mandatoryFieldsByOrder = {
  Account: 0,
  ClientAccount: 1,
  Currency: 2,
  PaymentMethod: 3,
  Amount: 4,
  ScheduleType: 5,
  TakeFromAccountOn: 6,
  StartDate: 7,
  Frequency: 8,
  SemiMonthlyDates: 9,
  Ending: 10,
  EndDate: 11,
  EndingAfterPayments: 12,
  Description: 13,
  MessageForRecipient: 14,
  TransactionLabel: 15,
  Notes: 16,
};
type FC = FunctionComponent<WithdrawAccountProps>;
const WithdrawAccountForm: FC = ({
  handlers,
  clientAccount,
  defaultValues,
  defaultTouches,
  showSubmitButton,
  isUnderReview,
  setIsUnderReview,
  hasBankAccount,
}) => {
  const { setNewCurrencyInfo } = useAccountInfo();
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);

  const [fieldErrorState, setFieldErrorState] = useState({
    Account: '',
    ClientAccount: '',
    Currency: '',
    PaymentMethod: '',
    Amount: '',
    ScheduleType: '',
    TakeFromAccountOn: '',
    StartDate: '',
    Frequency: '',
    SemiMonthlyDates: '',
    Ending: '',
    EndDate: '',
    EndingAfterPayments: '',
    Description: '',
    MessageForRecipient: '',
    TransactionLabel: '',
    Notes: '',
  });

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.WithdrawMyAccount.Form' });

  const handleFieldError = useCallback(
    (errorMessage: string | undefined, itemId: vp.WithdrawAccountId, isDatePicker = false) => {
      if ((errorMessage && fieldErrorState[itemId]) || (errorMessage && isDatePicker)) {
        return errorMessage;
      }
      return null;
    },
    [fieldErrorState],
  );
  const handleFieldFocus = (itemId: vp.WithdrawAccountId, errorMessages: FormikErrors<FormData>) => {
    Object.entries(mandatoryFieldsByOrder).forEach(([key, val]) => {
      if (mandatoryFieldsByOrder[itemId] >= val) {
        setFieldErrorState((prevState) => ({ ...prevState, [key]: errorMessages[key as vp.WithdrawAccountId] || '' }));
      }
    });
  };
  const withdrawAccountRow = buildWithdrawAccountRows({ moreInfoNeeded, clientAccount });
  const now = moment().format('YYYY-MM-DD');
  const { accountBalance } = useAccountSummary();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const [clientAccountBalanceResponse, getClientAccountBalance] = useFetchClientAccountBalance({});
  const {
    response: { AccountType, ClientAccountsEnabled, Country },
  } = useClientLogin();
  const { initPendingData, updatePendingData } = usePendingData();
  const availableBalance =
    Number(ClientAccountsEnabled) === 1 ? clientAccountBalanceResponse.AvailableFunds : accountBalance?.AvailableFunds;

  const ctx = useContext(AccountCreationContext);
  const [currency, setCurrency] = useState('CAD');

  useEffect(() => {
    if (clientAccount) {
      getClientAccountBalance({
        ClientAccountID: clientAccount.ClientAccountID,
        Currency: currency,
      });
    }
  }, [clientAccount, currency]);

  useEffect(() => {
    if (defaultValues.Currency !== '') {
      setCurrency(defaultValues?.Currency || 'CAD');
    }
  }, [defaultValues.Currency]);

  const addMoreInformation = () => {
    setMoreInfoNeeded(true);
  };
  const [showOverallErrorMessage, setShowOverallErrorMessage] = useState<boolean>(false);
  return (
    <Formik
      initialValues={defaultValues}
      initialTouched={defaultTouches}
      onSubmit={handlers.onWithdrawAccount}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize={vp.toBool(ClientAccountsEnabled)}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        submitForm,
        setTouched,
        setValues,
        handleSubmit,
        isValid,
        validateForm,
      }) => {
        useEffect(() => {
          handlers.onWithdrawChange(values, errors, touched, submitForm, withdrawAccountRow);
        }, [values, errors]);
        useEffect(() => {
          if (defaultValues.Account !== values.Account || values.PaymentMethod !== defaultValues.PaymentMethod) {
            setValues({ ...values, Account: '' });
            setTouched({ ...touched, Account: false });
          }
        }, [values.PaymentMethod]);
        useEffect(() => {
          if (
            defaultValues.Account !== values.Account ||
            values.PaymentMethod !== defaultValues.PaymentMethod ||
            values.Currency !== defaultValues.Currency
          ) {
            setTouched({ ...touched, Account: false });
          }
        }, [values.Currency]);
        useEffect(() => {
          initPendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, []);
        useEffect(() => {
          updatePendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, [values]);
        useEffect(() => {
          if (availableBalance && availableBalance !== '') {
            handleChange(vp.createSyntheticEvent('Amount', values.Amount));
          }
        }, [availableBalance]);
        useEffect(() => {
          void validateForm();
        }, [defaultValues, defaultTouches]);
        useEffect(() => {
          if (defaultValues.Account !== '') {
            setValues({ ...values, Account: defaultValues.Account });
            ctx.handleFieldChange(vp.createSyntheticEvent('Account', defaultValues.Account));
          }
        }, [defaultValues.Account]);
        const withdrawTable: vp.TableProps = {
          name: 'withdraw-account-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: withdrawAccountRow
            .filter((x) => x.visible(values))
            .map(
              ({
                label,
                id,
                require,
                preset,
                type,
                placeholder,
                getSelectOptions,
                maxLength,
                isReadOnly,
                disableFiltering,
                searchCallback,
                SelectLabel,
                onChangeCallBack,
              }) => {
                let fieldComponent;

                const fieldLabel = (
                  <Box jc="fs" flx="col">
                    <Box ai="center">
                      {label} {getRequiredIcon(require)}
                      {id === 'TakeFromAccountOn' && (
                        <Tooltip
                          type="hover"
                          information={t('TakeFromAccountOn.toolTip')}
                          position="right"
                          hasArrow={false}
                          icon="question"
                        />
                      )}
                    </Box>
                  </Box>
                );

                if (type === 'singleDate') {
                  fieldComponent = (
                    <div className={styles.datePickerContainer}>
                      <DatepickerSimple
                        name={id}
                        value={String(values[id])}
                        error={handleFieldError(errors[id], id)}
                        onBlur={handleBlur}
                        onChange={(e: { target: { value: string } }) => {
                          if (e.target && e.target.value === 'undefined') {
                            e.target.value = String(values[id]);
                          }
                          handleChange(e);
                        }}
                        isDisabled={isUnderReview}
                      />
                    </div>
                  );
                } else if (type === 'input') {
                  fieldComponent = (
                    <TextField
                      preset={preset}
                      key={id}
                      name={id}
                      value={String(values[id])}
                      variant="standard"
                      placeholder={placeholder}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      maxLength={maxLength}
                      error={handleFieldError(errors[id], id)}
                      onFocus={() => handleFieldFocus(id, errors)}
                      disabled={isUnderReview}
                      readOnly={isReadOnly}
                    />
                  );
                } else if (type === 'select') {
                  if (id === 'ClientAccount') {
                    fieldComponent = (
                      <Select
                        variant="standard"
                        readOnly
                        options={getSelectOptions(values)}
                        key={id}
                        name={id}
                        value={String(values[id])}
                        placeholder={placeholder}
                        disabled={isUnderReview}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={handleFieldError(errors[id], id)}
                        onFocus={() => handleFieldFocus(id, errors)}
                        isReadOnly={isReadOnly}
                      />
                    );
                  } else {
                    fieldComponent = (
                      <Select
                        variant="standard"
                        options={getSelectOptions(values)}
                        lockedValues={lockedFeatures.LockedSelectOptions}
                        lockedCallback={(e: string) => updateIsLocked(true, e)}
                        key={id}
                        name={id}
                        value={String(values[id])}
                        placeholder={placeholder}
                        disabled={isUnderReview}
                        noOrdering
                        onBlur={handleBlur}
                        disableFiltering={disableFiltering ? disableFiltering(values) : undefined}
                        searchCallback={
                          disableFiltering ? (disableFiltering(values) ? searchCallback : undefined) : undefined
                        }
                        labelOverride={SelectLabel ? SelectLabel(values) : undefined}
                        onChange={(e) => {
                          if (onChangeCallBack) {
                            onChangeCallBack(e, values);
                          }
                          handleChange(e);
                          id === 'Currency' ? setNewCurrencyInfo(e.target.value) : handleChange(e);
                        }}
                        error={handleFieldError(errors[id], id)}
                        onFocus={() => handleFieldFocus(id, errors)}
                        isReadOnly={isReadOnly}
                      />
                    );
                  }
                  // }
                } else {
                  fieldComponent = (
                    <Box width="100%" jc="fe">
                      <Toggle
                        name={id}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={Boolean(values[id])}
                        error={handleFieldError(errors[id], id)}
                        disabled={isUnderReview}
                      />
                    </Box>
                  );
                }
                return {
                  Field: fieldLabel,
                  Input: fieldComponent,
                };
              },
            ),
        };

        return (
          <>
            <Table {...withdrawTable} />
            {!moreInfoNeeded && (
              <div style={{ paddingRight: '1em' }}>
                <u>
                  <vp.Button
                    noShadow
                    id="add-more-information-button"
                    label={t('Buttons.addMoreInformation')}
                    color="secondary"
                    onClick={() => {
                      addMoreInformation();
                    }}
                  />
                </u>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <Box mt="md" jc="fe" gap="sm" ai="center">
                {showOverallErrorMessage ? (
                  <div className={styles.overallErrorMessage}>{t('ErrorMessage.completeRequiredFields')}</div>
                ) : null}
                {isUnderReview ? (
                  <vp.Button
                    id="Fund-back"
                    color="secondary"
                    label={t('Buttons.back')}
                    onClick={() => {
                      setIsUnderReview(false);
                    }}
                  />
                ) : null}
                <div
                  style={{ height: '41px' }}
                  onMouseEnter={() => {
                    if (!showSubmitButton || !isValid) {
                      setShowOverallErrorMessage(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!showSubmitButton || !isValid) {
                      setShowOverallErrorMessage(false);
                    }
                  }}
                >
                  <vp.Button
                    id="collection-submit"
                    isDisabled={!showSubmitButton || !isValid || !hasBankAccount}
                    type="submit"
                    label={isUnderReview ? t('Buttons.submit') : t('Buttons.review')}
                  />
                </div>
              </Box>
            </form>
          </>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required(),
      PaymentMethod: Yup.string().required(),
      Account: Number(ClientAccountsEnabled) === 1 ? Yup.string().notRequired() : Yup.string().required(),
      ClientAccount: Number(ClientAccountsEnabled) === 0 ? Yup.string().notRequired() : Yup.string().required(),
      Amount: Yup.string()
        .test('amount-validation', t('ErrorMessage.amountMinValue'), (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .test(
          'amount-validation',
          t('ErrorMessage.amountInsufficientFunds', { availableBalance: String(availableBalance) }),
          (value) => {
            const availableFundsValidation = Number(value) > Number(availableBalance) && AccountType !== 'PaaS';
            return !availableFundsValidation;
          },
        )
        .required(),

      ScheduleType: Yup.string().when(['PaymentMethod', 'Currency'], {
        is: (PaymentMethod: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      TakeFromAccountOn: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'single' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string()
          .test(
            'start-date-validation',
            t('ErrorMessage.takeFromAccountOnInvalidStartDate', { startDate: moment(now).format('YYYY-MM-DD') }),
            (value: any) => {
              const dateValidation = value !== 'underfined' && moment(Number(value)).format('YYYY-MM-DD') < now;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      StartDate: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string()
          .test(
            'start-date-validation',
            t('ErrorMessage.invalidStartDate', { startDate: moment(now).add(1, 'days').format('YYYY-MM-DD') }),
            (value: string | undefined) => {
              const dateValidation =
                value !== 'underfined' &&
                moment(Number(value)).format('YYYY-MM-DD') < moment(now).add(1, 'days').format('YYYY-MM-DD');
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      Frequency: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      SemiMonthlyDates: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Frequency'], {
        is: (PaymentMethod: string, ScheduleType: string, Frequency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          Frequency === 'semi-monthly' &&
          (Country === 'CA' || Country === 'US'),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Ending: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      EndDate: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Ending: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          Ending === 'End Date' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string()
          .test(
            'end-date-validation',
            t('ErrorMessage.invalidEndDate'),
            (value: string | undefined, StartDate: StartDateType) => {
              const endDate = new Date(moment(Number(value)).format('YYYY-MM-DD'));
              const startDate = new Date(moment(Number(StartDate.parent.StartDate)).format('YYYY-MM-DD'));
              const dateValidation = endDate === startDate || endDate < startDate;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      EndingAfterPayments: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending'], {
        is: (PaymentMethod: string, ScheduleType: string, Ending: string) =>
          PaymentMethod === 'Bank Account' && ScheduleType === 'recurring' && Ending === 'Payments',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Description: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency', 'TakeFromAccountOn'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string, TakeFromAccountOn: string) =>
          PaymentMethod === 'Bank Account' &&
          (ScheduleType === 'recurring' ||
            (TakeFromAccountOn && !vp.IsTodayDate(moment(Number(TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),

      TransactionLabel: Yup.string().max(30, t('ErrorMessage.transactionLabelMaximumLength')),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default WithdrawAccountForm;
